import { useState } from "react";
import { ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toBackendModuleKey, type BackendModuleKey } from "../../lib/backendModuleKeys";
import type { ModuleAccessKey, ModuleAccessMarker, PendingRequestRef } from "../../lib/accessRequests";
import { useAccessRequestMutations } from "../../hooks/useAccessRequests";
import { useGateAccess, useModuleAccess } from "../../hooks/useModuleAccess";
import type { ModuleGateId } from "../../lib/moduleGates";
import { isCatalogModuleRequestable } from "../../lib/moduleCatalog";
import { MODULE_GATES, gateRequestable } from "../../lib/moduleGates";
import { RequestAccessDialog } from "./RequestAccessDialog";
import {
  AccessGatePlaceholder,
  type AccessGatePlaceholderVariant,
} from "./AccessGatePlaceholders";

export type AccessGateProps = {
  module: ModuleAccessKey;
  marker?: ModuleAccessMarker | null;
  /** Explicit grant overrides marker / permission resolution when set. */
  granted?: boolean;
  pending?: PendingRequestRef | null;
  title?: string;
  className?: string;
  placeholder?: AccessGatePlaceholderVariant;
  isLoading?: boolean;
  requestable?: boolean;
  /** When true with requestable=false, show admin-only note instead of request CTA. */
  adminOnlyNote?: boolean;
  children: React.ReactNode;
};

/**
 * DEC-005: blurred placeholder + Solicită acces when the user lacks module access.
 * Never renders `children` when access is denied (no data in DOM).
 */
export function AccessGate({
  module,
  marker,
  granted: grantedOverride,
  pending: pendingOverride,
  title,
  className,
  placeholder = "inline",
  isLoading: isLoadingOverride,
  requestable: requestableOverride,
  adminOnlyNote = false,
  children,
}: AccessGateProps) {
  const resolveFromHook = grantedOverride === undefined;
  const resolved = useModuleAccess(module, {
    marker,
    fetchPending: resolveFromHook,
  });

  const granted = grantedOverride ?? resolved.granted;
  const pending = pendingOverride ?? resolved.pending;
  const isLoading = resolveFromHook
    ? (isLoadingOverride ?? resolved.isLoading)
    : (isLoadingOverride ?? false);

  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div
        className={className ?? "card p-6 h-28 animate-pulse bg-gray-100 dark:bg-gray-800 rounded-xl"}
        role="status"
        aria-label={t("common.loading", { defaultValue: "Se încarcă" })}
      />
    );
  }

  if (granted) {
    return <>{children}</>;
  }

  const requestable =
    requestableOverride ?? gateRequestable(module as ModuleGateId);
  const requestAccessModule = toBackendModuleKey(module);

  return (
    <BlurredAccessOverlay
      module={module}
      title={title}
      pending={pending}
      className={className}
      placeholder={placeholder}
      requestable={requestable}
      requestAccessModule={requestAccessModule}
      adminOnlyNote={adminOnlyNote}
    />
  );
}

type BlurredAccessOverlayProps = {
  module: ModuleAccessKey;
  title?: string;
  pending: PendingRequestRef | null;
  className?: string;
  placeholder: AccessGatePlaceholderVariant;
  requestable?: boolean;
  requestAccessModule?: BackendModuleKey | null;
  adminOnlyNote?: boolean;
};

export function BlurredAccessOverlay({
  module,
  title,
  pending,
  className,
  placeholder,
  requestable = true,
  requestAccessModule,
  adminOnlyNote = false,
}: BlurredAccessOverlayProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { cancelMutation } = useAccessRequestMutations();
  const canRequestAccess =
    requestable &&
    requestAccessModule != null &&
    isCatalogModuleRequestable(requestAccessModule);

  const heading =
    title ?? t(`accessRequests.modules.${module}`, { defaultValue: module });

  return (
    <>
      <div
        className={className ?? "relative rounded-xl overflow-hidden"}
        role="region"
        aria-label={heading}
        data-testid={`access-gate-${requestAccessModule ?? module}`}
      >
        <div className="pointer-events-none select-none blur-md opacity-70" aria-hidden inert>
          <AccessGatePlaceholder variant={placeholder} module={module} title={heading} />
        </div>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center bg-white/75 dark:bg-gray-900/80 backdrop-blur-[2px]"
        >
          <ShieldOff className="w-8 h-8 text-gray-400 dark:text-gray-500" aria-hidden />
          <div className="max-w-sm space-y-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{heading}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {adminOnlyNote && !requestable
                ? t("accessRequests.adminOnlyNote")
                : t("accessRequests.noAccessMessage")}
            </p>
          </div>
          {canRequestAccess ? (
            pending ? (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                  {t("accessRequests.pendingLabel")}
                </span>
                <button
                  type="button"
                  className="text-sm btn-secondary"
                  onClick={() => {
                    if (pending.id) cancelMutation.mutate(pending.id);
                  }}
                  disabled={cancelMutation.isPending}
                  aria-label={t("accessRequests.cancelRequestAria")}
                >
                  {t("accessRequests.cancelRequest")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setDialogOpen(true)}
              >
                {t("accessRequests.requestButton")}
              </button>
            )
          ) : null}
        </div>
      </div>
      {canRequestAccess ? (
        <RequestAccessDialog
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          moduleKey={requestAccessModule}
        />
      ) : null}
    </>
  );
}

type ModulePageGateProps = {
  gateId: ModuleGateId;
  placeholder: AccessGatePlaceholderVariant;
  title?: string;
  className?: string;
  children: React.ReactNode;
};

/** Page wrapper: mounts gated content only when access is granted. */
export function ModulePageGate({
  gateId,
  placeholder,
  title,
  className,
  children,
}: ModulePageGateProps) {
  const gate = MODULE_GATES[gateId];
  const { granted, pending } = useGateAccess(gateId);
  const placeholderModule = gate.placeholderModule;

  if (granted) {
    return <>{children}</>;
  }

  return (
    <BlurredAccessOverlay
      module={placeholderModule}
      title={title}
      pending={pending}
      className={className ?? "min-h-[50vh]"}
      placeholder={placeholder}
      requestable={gateRequestable(gateId)}
      requestAccessModule={gate.accessRequestModule}
      adminOnlyNote={!gateRequestable(gateId) && gate.accessRequestModule == null}
    />
  );
}

/** @deprecated Use AccessGate — kept for existing tests importing ModuleNoAccessCard. */
export function ModuleNoAccessCard({
  title,
  pending,
  onRequest,
  onCancel,
  cancelPending,
  className,
}: {
  title: string;
  pending: PendingRequestRef | null;
  onRequest: () => void;
  onCancel: () => void;
  cancelPending: boolean;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={
        className ??
        "card p-6 flex flex-col gap-3 border border-gray-200 dark:border-gray-700"
      }
      role="region"
      aria-label={title}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 shrink-0" aria-hidden>
          <ShieldOff className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            {t("accessRequests.noAccessMessage")}
          </p>
        </div>
      </div>
      {pending ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
            {t("accessRequests.pendingLabel")}
          </span>
          <button
            type="button"
            className="text-sm btn-secondary"
            onClick={onCancel}
            disabled={cancelPending}
            aria-label={t("accessRequests.cancelRequestAria")}
          >
            {t("accessRequests.cancelRequest")}
          </button>
        </div>
      ) : (
        <button type="button" className="btn-primary self-start" onClick={onRequest}>
          {t("accessRequests.requestButton")}
        </button>
      )}
    </div>
  );
}
